/**
 * DriveFolderPicker — modal de selecao de pasta do Google Drive.
 *
 * Fluxo:
 *   1. Abre sobre a SourcesPage (overlay full-screen).
 *   2. Exibe tabs "Meu Drive" / "Compartilhados comigo" no topo.
 *   3. Mostra breadcrumb + grid de pastas.
 *   4. Click em pasta navega para dentro (parentId atualizado).
 *   5. "Selecionar esta pasta" chama PUT /folder e fecha o picker.
 *   6. Suporta paginacao via nextPageToken (botao "Carregar mais").
 *   7. Trocar de tab reseta navegacao e recarrega a raiz com view correto.
 *
 * Acessibilidade:
 *   - role="dialog" + aria-modal + aria-labelledby.
 *   - Tabs: role="tablist" / role="tab" / aria-selected.
 *   - Setas esquerda/direita alternam tabs via teclado.
 *   - Escape fecha o modal.
 *   - focus fica dentro do modal enquanto aberto (focus trap simples).
 *   - aria-live="polite" anuncia estados de loading.
 *
 * i18n: zero string hardcoded — tudo via t().
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../lib/i18n';
import { apiClient, type DriveFolder, type ApiError } from '../lib/api-client';

// ---- tipos locais ----

type DriveView = 'mine' | 'shared';

interface BreadcrumbEntry {
  readonly id: string | null; // null = raiz
  readonly name: string;
}

type PickerLoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; folders: readonly DriveFolder[]; nextPageToken: string | undefined }
  | { kind: 'error'; code: 'network' | 'auth' | 'generic' };

interface DriveFolderPickerProps {
  readonly sourceId: string;
  /** Chamado quando o usuario fecha sem selecionar (Escape ou botao fechar). */
  readonly onClose: () => void;
  /** Chamado apos selecao bem-sucedida — recebe o folder confirmado. */
  readonly onFolderSelected: (folderId: string, folderName: string) => void;
}

// ---- helpers ----

function classifyError(err: unknown): 'network' | 'auth' | 'generic' {
  const e = err as Partial<ApiError>;
  if (e.status === 0 || e.code === 'network_error') return 'network';
  if (e.status === 401 || e.status === 403) return 'auth';
  return 'generic';
}

function errorKey(code: 'network' | 'auth' | 'generic'): Parameters<typeof t>[0] {
  if (code === 'network') return 'drivePicker.errorNetwork';
  if (code === 'auth') return 'drivePicker.errorAuth';
  return 'drivePicker.errorGeneric';
}

// ---- constantes de tabs ----

const TABS: ReadonlyArray<{ readonly id: DriveView; readonly labelKey: Parameters<typeof t>[0] }> =
  [
    { id: 'mine', labelKey: 'drivePicker.tabMine' },
    { id: 'shared', labelKey: 'drivePicker.tabShared' },
  ] as const;

// ---- componente ----

export function DriveFolderPicker({
  sourceId,
  onClose,
  onFolderSelected,
}: DriveFolderPickerProps): JSX.Element {
  // Tab ativa — default "mine"
  const [activeView, setActiveView] = useState<DriveView>('mine');

  // Pilha de navegacao: null = raiz
  const [breadcrumb, setBreadcrumb] = useState<readonly BreadcrumbEntry[]>([
    { id: null, name: t('drivePicker.back') },
  ]);
  const currentParentId = breadcrumb[breadcrumb.length - 1]?.id ?? null;

  const [loadState, setLoadState] = useState<PickerLoadState>({ kind: 'loading' });
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Para focus trap simples
  const dialogRef = useRef<HTMLDivElement>(null);
  const headingId = 'drive-picker-title';

  // Refs para os botoes de tab (navegacao por teclado)
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // ---- carrega pastas ----

  const loadFolders = useCallback(
    async (parentId: string | null, view: DriveView, pageToken?: string): Promise<void> => {
      setLoadState({ kind: 'loading' });
      setConfirmError(null);
      try {
        const res = await apiClient.getDriveFolders(
          sourceId,
          parentId ?? undefined,
          pageToken,
          view,
        );
        setLoadState({
          kind: 'ready',
          folders: res.folders,
          nextPageToken: res.nextPageToken,
        });
      } catch (err) {
        setLoadState({ kind: 'error', code: classifyError(err) });
      }
    },
    [sourceId],
  );

  // Carrega ao montar e sempre que parentId ou view mudar
  useEffect(() => {
    void loadFolders(currentParentId, activeView);
  }, [loadFolders, currentParentId, activeView]);

  // ---- troca de tab ----

  const handleTabChange = useCallback(
    (view: DriveView): void => {
      if (view === activeView) return;
      // Reseta navegacao para raiz
      setBreadcrumb([{ id: null, name: t('drivePicker.back') }]);
      setActiveView(view);
    },
    [activeView],
  );

  // Navegacao por teclado nas tabs (setas esquerda/direita)
  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const next = (index + 1) % TABS.length;
        tabRefs.current[next]?.focus();
        handleTabChange(TABS[next]!.id);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prev = (index - 1 + TABS.length) % TABS.length;
        tabRefs.current[prev]?.focus();
        handleTabChange(TABS[prev]!.id);
      }
    },
    [handleTabChange],
  );

  // ---- paginacao ----

  const handleLoadMore = useCallback(async (): Promise<void> => {
    if (loadState.kind !== 'ready' || loadState.nextPageToken === undefined) return;
    try {
      const res = await apiClient.getDriveFolders(
        sourceId,
        currentParentId ?? undefined,
        loadState.nextPageToken,
        activeView,
      );
      setLoadState({
        kind: 'ready',
        folders: [...loadState.folders, ...res.folders],
        nextPageToken: res.nextPageToken,
      });
    } catch (err) {
      setLoadState({ kind: 'error', code: classifyError(err) });
    }
  }, [loadState, sourceId, currentParentId, activeView]);

  // ---- navegacao em pastas ----

  const handleEnterFolder = useCallback((folder: DriveFolder): void => {
    setBreadcrumb((prev) => [...prev, { id: folder.id, name: folder.name }]);
  }, []);

  const handleBreadcrumbNav = useCallback((index: number): void => {
    setBreadcrumb((prev) => prev.slice(0, index + 1));
  }, []);

  // ---- selecao ----

  const handleSelect = useCallback(
    async (folderId: string): Promise<void> => {
      setConfirming(true);
      setConfirmError(null);
      try {
        const res = await apiClient.setDriveFolder(sourceId, folderId);
        onFolderSelected(res.folder.id, res.folder.name);
      } catch {
        setConfirmError(t('drivePicker.confirmError'));
        setConfirming(false);
      }
    },
    [sourceId, onFolderSelected],
  );

  // ---- a11y: fechar com Escape ----

  useEffect(() => {
    const handle = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [onClose]);

  // ---- a11y: foco inicial no dialog ----

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  // ---- render ----

  const currentFolderName =
    breadcrumb.length > 1 ? (breadcrumb[breadcrumb.length - 1]?.name ?? '') : null;

  const emptyKey: Parameters<typeof t>[0] =
    activeView === 'shared' ? 'drivePicker.emptyShared' : 'drivePicker.noFolders';

  return (
    <div
      className="picker-backdrop"
      role="presentation"
      onClick={onClose}
      data-testid="drive-picker-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="picker-dialog"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        data-testid="drive-picker-dialog"
      >
        {/* Cabecalho */}
        <header className="picker-header">
          <h2 id={headingId}>{t('drivePicker.title')}</h2>
          <p className="picker-subtitle">{t('drivePicker.subtitle')}</p>
          <button
            type="button"
            className="picker-close"
            aria-label={t('dialog.close')}
            onClick={onClose}
          >
            &times;
          </button>
        </header>

        {/* Tabs: Meu Drive / Compartilhados comigo */}
        <div
          role="tablist"
          aria-label={t('drivePicker.title')}
          className="picker-tabs"
          data-testid="picker-tablist"
        >
          {TABS.map((tab, index) => (
            <button
              key={tab.id}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              role="tab"
              id={`picker-tab-${tab.id}`}
              aria-selected={activeView === tab.id}
              aria-controls="picker-tabpanel"
              type="button"
              className={`picker-tab${activeView === tab.id ? ' picker-tab--active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
              onKeyDown={(e) => handleTabKeyDown(e, index)}
              tabIndex={activeView === tab.id ? 0 : -1}
              data-testid={`picker-tab-${tab.id}`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        {/* Breadcrumb */}
        <nav aria-label="Navegacao de pastas" className="picker-breadcrumb">
          <ol>
            {breadcrumb.map((entry, idx) => {
              const isLast = idx === breadcrumb.length - 1;
              return (
                <li key={`${entry.id ?? 'root'}-${idx}`}>
                  {isLast ? (
                    <span aria-current="page">{entry.name}</span>
                  ) : (
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => handleBreadcrumbNav(idx)}
                    >
                      {entry.name}
                    </button>
                  )}
                  {!isLast ? <span aria-hidden="true"> / </span> : null}
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Estado vivo para leitores de tela */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {loadState.kind === 'loading' ? t('drivePicker.loading') : null}
        </div>

        {/* Conteudo */}
        <div
          id="picker-tabpanel"
          role="tabpanel"
          aria-labelledby={`picker-tab-${activeView}`}
          className="picker-body"
        >
          {/* Erro de confirmacao */}
          {confirmError !== null ? (
            <p role="alert" className="picker-error">
              {confirmError}
            </p>
          ) : null}

          {loadState.kind === 'loading' ? (
            <p role="status" className="picker-loading" data-testid="picker-loading">
              {t('drivePicker.loading')}
            </p>
          ) : null}

          {loadState.kind === 'error' ? (
            <div className="picker-empty" data-testid="picker-error">
              <p role="alert">{t(errorKey(loadState.code))}</p>
              <button type="button" onClick={() => void loadFolders(currentParentId, activeView)}>
                {t('common.error')}
              </button>
            </div>
          ) : null}

          {loadState.kind === 'ready' ? (
            <>
              {loadState.folders.length === 0 ? (
                <div className="picker-empty" data-testid="picker-empty">
                  <p>{t(emptyKey)}</p>
                  {activeView === 'mine' ? (
                    <a href="https://drive.google.com" target="_blank" rel="noopener noreferrer">
                      {t('drivePicker.openDrive')}
                    </a>
                  ) : null}
                </div>
              ) : (
                <ul
                  className="picker-folders-grid"
                  aria-label="Pastas disponíveis"
                  data-testid="picker-folders-list"
                >
                  {loadState.folders.map((folder) => (
                    <li key={folder.id} className="picker-folder-item">
                      <div className="picker-folder-info">
                        <span className="picker-folder-icon" aria-hidden="true" role="img">
                          📁
                        </span>
                        <button
                          type="button"
                          className="picker-folder-name btn-link"
                          onClick={() => handleEnterFolder(folder)}
                          data-testid={`folder-nav-${folder.id}`}
                        >
                          {folder.name}
                        </button>
                      </div>
                      <button
                        type="button"
                        className="picker-select-btn btn-primary"
                        onClick={() => void handleSelect(folder.id)}
                        disabled={confirming}
                        aria-label={`${t('drivePicker.selectThisFolder')}: ${folder.name}`}
                        data-testid={`folder-select-${folder.id}`}
                      >
                        {t('drivePicker.selectThisFolder')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Botao "Selecionar esta pasta" para o nivel atual (breadcrumb > raiz) */}
              {currentFolderName !== null && currentParentId !== null ? (
                <div className="picker-select-current">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => void handleSelect(currentParentId)}
                    disabled={confirming}
                    data-testid="select-current-folder"
                  >
                    {confirming ? t('common.loading') : t('drivePicker.confirmSelection')}
                  </button>
                </div>
              ) : null}

              {/* Paginacao */}
              {loadState.nextPageToken !== undefined ? (
                <div className="picker-load-more">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => void handleLoadMore()}
                    data-testid="picker-load-more"
                  >
                    {t('drivePicker.loadMore')}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        {/* Rodape */}
        <footer className="picker-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('drivePicker.back')}
          </button>
        </footer>
      </div>
    </div>
  );
}
